import { Col, Divider, Flex, Form, Tooltip, Typography } from "antd";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { useState } from "react";

import {
  CONTRACT_CURRENCY_ENUM,
  CurrencyType,
  getNetworksAsOptionsByCurrency,
  NETWORK_TEXT,
  NetworkType,
  RISK_LEVEL_ENUM,
  WalletRiskLevel,
} from "interfaces";
import { DefaultInput, DefaultSelect, NumberInput, getCryptoIconRound } from "ui";
import {
  _isRequired,
  _greaterThan,
  _isNumericWithPrecision,
  renderSelectOptions,
  _isValidNetworkAddress,
  getWalletRiskMessage,
} from "utils";
import { AmlAlert } from "components/AmlAlert";
import { trimMask } from "utils";

interface IProps {
  disabled: boolean;
  addressInputVisible: boolean;
  hidden?: boolean;
  className?: string;
  onWalletChange?: (value: string) => void;
  showRiskAlert?: boolean;
  riskLevel?: WalletRiskLevel;
  title: string;
}

export const CreateBidForm = (props: IProps) => {
  const {
    disabled,
    addressInputVisible,
    hidden = false,
    className = "",
    onWalletChange,
    showRiskAlert,
    riskLevel,
    title,
  } = props;

  const form = Form.useFormInstance();
  const [selectedNetwork, setSelectedNetwork] = useState<NetworkType>(form.getFieldValue("network") ?? undefined);
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyType>(form.getFieldValue("currency") ?? undefined);

  const handleNetworkChange = () => {
    const network = form.getFieldValue("network") as NetworkType;
    const currentWallet = form.getFieldValue("destinationWallet");

    setSelectedNetwork(network);

    if (currentWallet) {
      form.validateFields(["destinationWallet"]);
    }
  };

  const handleCurrencyChange = () => {
    const currency = form.getFieldValue("currency") as CurrencyType;

    setSelectedCurrency(currency);
    form.setFieldValue("network", undefined);
    setSelectedNetwork(undefined);
  };

  if (hidden) {
    return null;
  }

  return (
    <Flex vertical className={className}>
      <Typography.Title level={2}>{title}</Typography.Title>
      <Flex gap="large">
        <Form.Item
          name="currency"
          rules={[_isRequired("Выберите тип валюты")]}
          validateDebounce={FORM_VALIDATION_DEBOUNCE}
          className="full-width"
          children={
            <DefaultSelect placeholder="Тип валюты" disabled={disabled} size="large" onChange={handleCurrencyChange}>
              {renderSelectOptions(CONTRACT_CURRENCY_ENUM, (key) => getCryptoIconRound(key))}
            </DefaultSelect>
          }
        />
        <Form.Item
          name="amount"
          rules={[_isRequired("Введите сумму выплаты в криптовалюте"), _greaterThan(0), _isNumericWithPrecision(12)]}
          validateDebounce={FORM_VALIDATION_DEBOUNCE}
          className="full-width"
        >
          <NumberInput placeholder="Сумма" disabled={disabled} />
        </Form.Item>
      </Flex>
      <Tooltip title={selectedCurrency ? "" : "Выберите тип криптовалюты"}>
        <Form.Item name="network" rules={[_isRequired("Выберите сеть")]} validateDebounce={FORM_VALIDATION_DEBOUNCE}>
          <DefaultSelect
            placeholder="Сеть блокчейн"
            disabled={disabled || !selectedCurrency}
            size="large"
            onChange={handleNetworkChange}
          >
            {selectedCurrency &&
              renderSelectOptions(
                getNetworksAsOptionsByCurrency(selectedCurrency, (network) => getCryptoIconRound(network))
              )}
          </DefaultSelect>
        </Form.Item>
      </Tooltip>
      {addressInputVisible && (
        <Col id="create-bid-destination-wallet-input">
          <Form.Item
            name="destinationWallet"
            rules={[_isRequired("Введите кошелек"), _isValidNetworkAddress(selectedNetwork)]}
            validateDebounce={FORM_VALIDATION_DEBOUNCE}
          >
            <DefaultInput
              placeholder={`Кошелек получателя${selectedNetwork ? ` в сети ${NETWORK_TEXT[selectedNetwork]}` : ""}`}
              disabled={disabled}
              onChange={(e) => {
                onWalletChange?.(e.target.value);
              }}
              mask={trimMask}
            />
          </Form.Item>
          {showRiskAlert && riskLevel && riskLevel !== RISK_LEVEL_ENUM.TIMEOUT && (
            <AmlAlert amlMessage={getWalletRiskMessage(riskLevel)} />
          )}
        </Col>
      )}
      <Divider className="margin-none" />
    </Flex>
  );
};
